import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
    return (
        <>
            <SEO
                title="Privacy Policy - Mega Property Solutions"
                description="Learn how Mega Property Solutions collects, uses, and protects your personal information. Our privacy policy explains our data practices for website visitors and customers."
                keywords="privacy policy, data protection, personal information, Mega Property Solutions, Auckland"
            />
            <main className="min-h-screen bg-brand-cream py-24">
                <div className="max-w-4xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="text-4xl md:text-5xl font-bold text-brand-green mb-8">
                            Privacy Policy
                        </h1>

                        <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
                            <div className="text-sm text-gray-600 mb-6">
                                <p><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-NZ')}</p>
                                <p><strong>Effective Date:</strong> {new Date().toLocaleDateString('en-NZ')}</p>
                            </div>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">1. Introduction</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    Mega Property Solutions ("we," "our," or "us") is committed to protecting your privacy.
                                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                                    when you visit our website meganz.co.nz or use our services.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">2. Information We Collect</h2>

                                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Personal Information</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We may collect personal information that you voluntarily provide to us, including:
                                </p>
                                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                                    <li>Name and contact information (email address, phone number)</li>
                                    <li>Property address and service requirements</li>
                                    <li>Communication preferences</li>
                                    <li>Any other information you choose to provide</li>
                                </ul>

                                <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Automatically Collected Information</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    When you visit our website, we automatically collect certain information, including:
                                </p>
                                <ul className="list-disc list-inside text-gray-700 space-y-2">
                                    <li>IP address and location data</li>
                                    <li>Browser type and version</li>
                                    <li>Device information (mobile, desktop, tablet)</li>
                                    <li>Pages visited and time spent on our website</li>
                                    <li>Referring website information</li>
                                    <li>Cookies and similar tracking technologies</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">3. How We Use Your Information</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We use the information we collect for the following purposes:
                                </p>
                                <ul className="list-disc list-inside text-gray-700 space-y-2">
                                    <li>Provide and improve our insulation, garage door, and home inspection services</li>
                                    <li>Respond to your inquiries and service requests</li>
                                    <li>Schedule appointments and assessments</li>
                                    <li>Send you service updates and relevant information</li>
                                    <li>Analyze website usage to improve user experience</li>
                                    <li>Comply with legal obligations</li>
                                    <li>Protect against fraud and security threats</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">4. Information Sharing and Disclosure</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We do not sell, trade, or rent your personal information to third parties. We may share
                                    your information in the following circumstances:
                                </p>
                                <ul className="list-disc list-inside text-gray-700 space-y-2">
                                    <li><strong>Service Providers:</strong> With trusted third parties who assist us in operating our website and providing services</li>
                                    <li><strong>Google Analytics:</strong> We use Google Analytics to analyze website traffic and user behavior</li>
                                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                                    <li><strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of our business</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">5. Data Storage and Security</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    We implement appropriate technical and organizational measures to protect your personal
                                    information against unauthorized access, alteration, disclosure, or destruction. Your data
                                    is stored securely on AWS servers and Google Analytics platforms, both of which maintain
                                    high security standards.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">6. Cookies and Tracking Technologies</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Our website uses cookies and similar technologies to enhance your browsing experience.
                                    We use Google Analytics cookies to:
                                </p>
                                <ul className="list-disc list-inside text-gray-700 space-y-2">
                                    <li>Track website traffic and user behavior</li>
                                    <li>Analyze which pages are most popular</li>
                                    <li>Understand how users navigate our website</li>
                                    <li>Improve website performance and user experience</li>
                                </ul>
                                <p className="text-gray-700 leading-relaxed mt-4">
                                    You can control cookie settings through your browser preferences. However, disabling
                                    cookies may affect website functionality.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">7. Your Rights</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Under New Zealand's Privacy Act 2020, you have the right to:
                                </p>
                                <ul className="list-disc list-inside text-gray-700 space-y-2">
                                    <li>Access your personal information we hold</li>
                                    <li>Request correction of inaccurate information</li>
                                    <li>Request deletion of your personal information</li>
                                    <li>Object to processing of your personal information</li>
                                    <li>Withdraw consent for data processing</li>
                                    <li>Lodge a complaint with the Privacy Commissioner</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">8. Data Retention</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    We retain your personal information only for as long as necessary to fulfill the purposes
                                    outlined in this Privacy Policy, unless a longer retention period is required by law.
                                    Google Analytics data is retained according to Google's data retention policies.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">9. Third-Party Links</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    Our website may contain links to third-party websites. We are not responsible for the
                                    privacy practices or content of these external sites. We encourage you to review the
                                    privacy policies of any third-party sites you visit.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">10. Children's Privacy</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    Our services are not directed to children under 16 years of age. We do not knowingly
                                    collect personal information from children under 16. If you become aware that a child
                                    has provided us with personal information, please contact us immediately.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">11. Changes to This Privacy Policy</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    We may update this Privacy Policy from time to time. We will notify you of any changes
                                    by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                                    We encourage you to review this Privacy Policy periodically for any changes.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-brand-green mb-4">12. Contact Us</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    If you have any questions about this Privacy Policy or our data practices, please contact us:
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-gray-700"><strong>Mega Property Solutions</strong></p>
                                    <p className="text-gray-700">Email: operation@meganz.co.nz</p>
                                    <p className="text-gray-700">Phone: 021-520-201 / 021-229-8869</p>
                                    <p className="text-gray-700">Website: www.meganz.co.nz</p>
                                </div>
                            </section>
                        </div>
                    </motion.div>
                </div>
            </main>
        </>
    );
};

export default PrivacyPolicy;
